import React, { useState, useEffect } from "react";
import type { JSX } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  Select,
  MenuItem,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import axiosInstance from "../../axios/axios";
import moment from "moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SkeletonLoader from "../../modules/dashboard/shared/skeleton_loader";
import WhiteTooltip from "../WhiteTooltip";

interface CVEDetailsPanelProps {
  cveData: any;
  isLoading: boolean;
  isNavigationFromAssets?: boolean;
}

const CVEDetailsPanel: React.FC<CVEDetailsPanelProps> = ({
  cveData,
  isLoading,
  isNavigationFromAssets,
}) => {
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [cvssVersion, setCvssVersion] = useState<string>("");
  const [authority, setAuthority] = useState<string>("");
  const [cvssOpen, setCvssOpen] = useState(false);
  const [authorityOpen, setAuthorityOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  console.log("CVEDetailsPanel: cveData", cveData);
  const location = useLocation();
  const isAssetsPage = location.pathname === "/on-premises/assets";

  useEffect(() => {
    if (
      cveData?.summaryData?.severity_metrics &&
      cveData.summaryData.severity_metrics.length > 0
    ) {
      console.log(
        "CVEDetailsPanel: Initializing with severity_metrics",
        cveData.summaryData.severity_metrics
      );

      // Filter metrics to include only those with meaningful data
      const validMetrics = cveData.summaryData.severity_metrics.filter(
        (metric: any) => {
          const hasAuthority = metric.authority && metric.authority.length > 0;
          const hasScoreData = metric.score && Object.keys(metric.score).length > 0;
          const hasValueData = metric.value && Object.keys(metric.value).length > 0;

          // Only show if it has meaningful data (not just empty arrays/objects)
          if (hasAuthority) return true;

          if (hasScoreData) {
            // Check if any score data actually has meaningful values
            const scoreKeys = Object.keys(metric.score);
            for (const key of scoreKeys) {
              const scoreData = metric.score[key];
              if (scoreData && scoreData.length > 0) {
                // Check if any score item has a meaningful value
                for (const item of scoreData) {
                  if (item.name === "cvss" && item.value !== null && item.value !== undefined && item.value > 0) {
                    return true;
                  }
                  if (item.name === "severity" && item.value && item.value !== "Low" && item.value !== "None") {
                    return true;
                  }
                }
              }
            }
          }

          if (hasValueData) {
            // Check if any value data actually has meaningful values
            const valueKeys = Object.keys(metric.value);
            for (const key of valueKeys) {
              const values = metric.value[key];
              if (values && values.length > 0) {
                // Check if any value has meaningful content (not just empty strings or "None"
                for (const value of values) {
                  if (value.value && value.value !== "" && value.value !== "None" && value.value !== "Low") {
                    return true;
                  }
                }
              }
            }
          }

          return false;
        }
      );

      if (validMetrics.length > 0) {
        // Find the first metric that has authorities
        const metricsWithAuthority = validMetrics.filter(
          (metric: any) => metric.authority && metric.authority.length > 0
        );

        console.log(
          "CVEDetailsPanel: metricsWithAuthority",
          metricsWithAuthority
        );

        if (metricsWithAuthority.length > 0) {
          const defaultMetric = metricsWithAuthority[0];
          console.log("CVEDetailsPanel: Setting default metric", defaultMetric);
          setCvssVersion(defaultMetric.key);
          setAuthority(defaultMetric.authority[0]?.key || "");
        } else {
          // Fallback to first available valid metric (even without authorities)
          const firstMetric = validMetrics[0];
          console.log("CVEDetailsPanel: Using fallback metric without authorities", firstMetric);
          if (firstMetric) {
            setCvssVersion(firstMetric.key);
            setAuthority(""); // No authority available, but data might still be shown
          } else {
            setCvssVersion("");
            setAuthority("");
          }
        }
      } else {
        setCvssVersion("");
        setAuthority("");
      }
    } else {
      console.log("CVEDetailsPanel: No severity_metrics available");
      setCvssVersion("");
      setAuthority("");
    }
  }, [cveData]);

  // Update authority when CVSS version changes
  useEffect(() => {
    console.log("CVSS version changed:", { cvssVersion, authority });
    if (cvssVersion && cveData?.summaryData?.severity_metrics) {
      const currentMetric = cveData.summaryData.severity_metrics.find(
        (metric: any) => metric.key === cvssVersion
      );
      console.log("Found currentMetric for CVSS version:", currentMetric);

      // Check if the current metric has any meaningful data
      if (currentMetric) {
        const hasAuthority = currentMetric.authority && currentMetric.authority.length > 0;
        const hasScoreData = currentMetric.score && Object.keys(currentMetric.score).length > 0;
        const hasValueData = currentMetric.value && Object.keys(currentMetric.value).length > 0;

        let hasMeaningfulData = hasAuthority;

        if (!hasMeaningfulData && hasScoreData) {
          // Check if any score data actually has meaningful values
          const scoreKeys = Object.keys(currentMetric.score);
          for (const key of scoreKeys) {
            const scoreData = currentMetric.score[key];
            if (scoreData && scoreData.length > 0) {
              for (const item of scoreData) {
                if (item.name === "cvss" && item.value !== null && item.value !== undefined && item.value > 0) {
                  hasMeaningfulData = true;
                  break;
                }
                if (item.name === "severity" && item.value && item.value !== "Low" && item.value !== "None") {
                  hasMeaningfulData = true;
                  break;
                }
              }
            }
            if (hasMeaningfulData) break;
          }
        }

        if (!hasMeaningfulData && hasValueData) {
          // Check if any value data actually has meaningful values
          const valueKeys = Object.keys(currentMetric.value);
          for (const key of valueKeys) {
            const values = currentMetric.value[key];
            if (values && values.length > 0) {
              // Check if any value has meaningful content
              for (const value of values) {
                if (value.value && value.value !== "" && value.value !== "None" && value.value !== "Low") {
                  hasMeaningfulData = true;
                  break;
                }
              }
              if (hasMeaningfulData) break;
            }
          }
        }

        // If the current metric has no meaningful data, find a valid one
        if (!hasMeaningfulData) {
          console.log("Current CVSS version has no meaningful data, finding valid alternative");
          const validMetrics = cveData.summaryData.severity_metrics.filter(
            (metric: any) => {
              const hasAuth = metric.authority && metric.authority.length > 0;
              const hasScore = metric.score && Object.keys(metric.score).length > 0;
              const hasValue = metric.value && Object.keys(metric.value).length > 0;

              if (hasAuth) return true;

              if (hasScore) {
                const scoreKeys = Object.keys(metric.score);
                for (const key of scoreKeys) {
                  const scoreData = metric.score[key];
                  if (scoreData && scoreData.length > 0) {
                    for (const item of scoreData) {
                      if (item.name === "cvss" && item.value !== null && item.value !== undefined && item.value > 0) {
                        return true;
                      }
                      if (item.name === "severity" && item.value && item.value !== "Low" && item.value !== "None") {
                        return true;
                      }
                    }
                  }
                }
              }

              if (hasValue) {
                const valueKeys = Object.keys(metric.value);
                for (const key of valueKeys) {
                  const values = metric.value[key];
                  if (values && values.length > 0) {
                    // Check if any value has meaningful content
                    for (const value of values) {
                      if (value.value && value.value !== "" && value.value !== "None" && value.value !== "Low") {
                        return true;
                      }
                    }
                  }
                }
              }

              return false;
            }
          );

          if (validMetrics.length > 0) {
            const newMetric = validMetrics[0];
            console.log("Setting new valid metric:", newMetric);
            setCvssVersion(newMetric.key);
            if (newMetric.authority && newMetric.authority.length > 0) {
              setAuthority(newMetric.authority[0].key);
            } else {
              setAuthority("");
            }
            return;
          }
        }

        if (hasAuthority) {
          // Only set authority if it's not already set or if current authority is not available for this CVSS version
          const currentAuthorityExists = currentMetric.authority.some(
            (auth: any) => auth.key === authority
          );
          console.log("Authority check:", {
            currentAuthority: authority,
            availableAuthorities: currentMetric.authority,
            currentAuthorityExists,
          });
          if (!authority || !currentAuthorityExists) {
            const newAuthority = currentMetric.authority[0].key;
            console.log("Setting new authority:", newAuthority);
            setAuthority(newAuthority);
          }
        } else {
          console.log("No authorities available for this CVSS version");
          setAuthority("");
        }
      }
    }
  }, [cvssVersion, cveData]);

  // Helper functions
  const getCurrentVersionMetrics = () => {
    const metrics = cveData.summaryData?.severity_metrics?.find(
      (metric: any) => metric.key === cvssVersion
    );
    console.log("getCurrentVersionMetrics:", {
      cvssVersion,
      authority,
      metrics,
    });
    return metrics;
  };

  const getCurrentScore = () => {
    const metrics = getCurrentVersionMetrics();
    if (!metrics) {
      console.log("getCurrentScore: No metrics", { cvssVersion, authority });
      return null;
    }

    // If authority is selected, use that authority's data
    if (authority) {
      const scoreData = (metrics.score as any)?.[authority];
      console.log("getCurrentScore: scoreData for authority", {
        authority,
        scoreData,
      });
      if (scoreData && scoreData.length > 0) {
        const scoreItem = scoreData.find((item: any) => item.name === "cvss");
        console.log("getCurrentScore: scoreItem", scoreItem);
        return scoreItem?.value;
      }
      return null;
    }

    // If no authority is selected, find the first available score data
    console.log("getCurrentScore: No authority selected, checking available scores", metrics.score);
    const scoreKeys = Object.keys(metrics.score || {});
    if (scoreKeys.length > 0) {
      // Find the first authority with non-zero score
      for (const scoreKey of scoreKeys) {
        const scoreData = (metrics.score as any)[scoreKey];
        if (scoreData && scoreData.length > 0) {
          const scoreItem = scoreData.find((item: any) => item.name === "cvss");
          if (scoreItem && scoreItem.value > 0) {
            console.log("getCurrentScore: Found score without authority", {
              scoreKey,
              scoreItem,
            });
            return scoreItem.value;
          }
        }
      }
      // If no non-zero scores, return the first available score
      const firstScoreKey = scoreKeys[0];
      const firstScoreData = (metrics.score as any)[firstScoreKey];
      if (firstScoreData && firstScoreData.length > 0) {
        const scoreItem = firstScoreData.find((item: any) => item.name === "cvss");
        console.log("getCurrentScore: Using first available score", {
          firstScoreKey,
          scoreItem,
        });
        return scoreItem?.value;
      }
    }
    return null;
  };

  const getCurrentSeverity = () => {
    const metrics = getCurrentVersionMetrics();
    if (!metrics) {
      console.log("getCurrentSeverity: No metrics", { cvssVersion, authority });
      return null;
    }

    // If authority is selected, use that authority's data
    if (authority) {
      const scoreData = (metrics.score as any)?.[authority];
      console.log("getCurrentSeverity: scoreData for authority", {
        authority,
        scoreData,
      });
      if (scoreData && scoreData.length > 0) {
        const severityItem = scoreData.find(
          (item: any) => item.name === "severity"
        );
        console.log("getCurrentSeverity: severityItem", severityItem);
        return severityItem?.value;
      }
      return null;
    }

    // If no authority is selected, find the first available severity data
    console.log("getCurrentSeverity: No authority selected, checking available scores", metrics.score);
    const scoreKeys = Object.keys(metrics.score || {});
    if (scoreKeys.length > 0) {
      // Find the first authority with non-zero score
      for (const scoreKey of scoreKeys) {
        const scoreData = (metrics.score as any)[scoreKey];
        if (scoreData && scoreData.length > 0) {
          const scoreItem = scoreData.find((item: any) => item.name === "cvss");
          const severityItem = scoreData.find((item: any) => item.name === "severity");
          if (scoreItem && scoreItem.value > 0 && severityItem) {
            console.log("getCurrentSeverity: Found severity without authority", {
              scoreKey,
              severityItem,
            });
            return severityItem.value;
          }
        }
      }
      // If no non-zero scores, return the first available severity
      const firstScoreKey = scoreKeys[0];
      const firstScoreData = (metrics.score as any)[firstScoreKey];
      if (firstScoreData && firstScoreData.length > 0) {
        const severityItem = firstScoreData.find(
          (item: any) => item.name === "severity"
        );
        console.log("getCurrentSeverity: Using first available severity", {
          firstScoreKey,
          severityItem,
        });
        return severityItem?.value;
      }
    }
    return null;
  };

  const getCurrentValues = () => {
    const metrics = getCurrentVersionMetrics();
    if (!metrics) {
      console.log("getCurrentValues: No metrics", { cvssVersion, authority });
      return [];
    }

    // If authority is selected, use that authority's data
    if (authority) {
      const values = (metrics.value as any)?.[authority] || [];
      console.log("getCurrentValues: values for authority", {
        authority,
        values,
      });
      return values;
    }

    // If no authority is selected, find the first available values data
    console.log("getCurrentValues: No authority selected, checking available values", metrics.value);
    const valueKeys = Object.keys(metrics.value || {});
    if (valueKeys.length > 0) {
      // Find the first authority with non-empty values
      for (const valueKey of valueKeys) {
        const values = (metrics.value as any)[valueKey] || [];
        if (values.length > 0) {
          console.log("getCurrentValues: Found values without authority", {
            valueKey,
            values,
          });
          return values;
        }
      }
    }
    return [];
  };

  // Get the current data source (which authority is being used)
  const getCurrentDataSource = () => {
    const metrics = getCurrentVersionMetrics();
    if (!metrics) return null;

    // If authority is selected, return that
    if (authority) return authority;

    // If no authority is selected, find the first available data source
    const scoreKeys = Object.keys(metrics.score || {});
    if (scoreKeys.length > 0) {
      // Find the first authority with non-zero score
      for (const scoreKey of scoreKeys) {
        const scoreData = (metrics.score as any)[scoreKey];
        if (scoreData && scoreData.length > 0) {
          const scoreItem = scoreData.find((item: any) => item.name === "cvss");
          if (scoreItem && scoreItem.value > 0) {
            return scoreKey;
          }
        }
      }
      // If no non-zero scores, return the first available
      return scoreKeys[0];
    }
    return null;
  };
  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return "#DC2626";
      case "high":
        return "#F97316";
      case "medium":
        return "#FBBF24";
      case "low":
        return "#22C55E";
      case "none":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  };
  // Metrics helpers
  const getMetricColor = (value: string) => {
    switch (value?.toLowerCase()) {
      case "network":
      case "high":
      case "none":
        return "#DC2626";
      case "low":
        return "#22C55E";
      case "unchanged":
        return "#3B82F6";
      case "changed":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  };
  const getAttackMetrics = () => {
    const values = getCurrentValues();
    if (!values.length) return [];
    return values
      .filter((v: any) => [1, 2, 3, 4].includes(v.row) && v.col === 1)
      .map((v: any) => ({
        label: v.name.split(" (")[0],
        value: v.value,
        color: getMetricColor(v.value),
      }));
  };
  const getImpactMetrics = () => {
    const values = getCurrentValues();
    if (!values.length) return [];
    return values
      .filter((v: any) => [1, 2, 3, 4].includes(v.row) && v.col === 2)
      .map((v: any) => ({
        label: v.name.split(" (")[0],
        value: v.value,
        color: getMetricColor(v.value),
      }));
  };
  const attackMetrics = getAttackMetrics();
  const impactMetrics = getImpactMetrics();
  const currentScore = getCurrentScore();
  const currentSeverity = getCurrentSeverity();

  const [matchingCpes, setMatchingCpes] = useState<{ [key: number]: string[] }>(
    {}
  );
  const [openCpeIndex, setOpenCpeIndex] = useState<number | null>(null);
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const [loadingCpeIndex, setLoadingCpeIndex] = useState<number | null>(null);

  const handleMatchingCpe = async (id: any, index: number) => {
    if (openCpeIndex === index) {
      setOpenCpeIndex(null); // Toggle off if already open
      return;
    }
    if (matchingCpes[index]) {
      setOpenCpeIndex(index);
      return;
    }
    setLoadingCpeIndex(index); // Start loading
    try {
      const response = await axiosInstance.get(
        `securityscan/cve/cpe-list?match_criteria_id=${id}`
      );
      if (response.status === 200) {
        setMatchingCpes((prev) => ({
          ...prev,
          [index]: response.data?.data?.cpe_list || [],
        }));
        setOpenCpeIndex(index);
      }
    } catch (error) {
      console.error("CVEDetailsPanel: Error fetching CPE list", error);
    } finally {
      setLoadingCpeIndex(null); // Stop loading
    }
  };

  return (
    <Box>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "400px",
            width: "100%",
          }}
        >
          <SkeletonLoader count={13} styles={{ height: 40, width: "100%" }} />
        </Box>
      ) : !cveData.summaryData ? (
        <>
          {
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                No data available
              </Typography>
            </Box>
          }
        </>
      ) : (
        <Box
          sx={{
            overflow: "auto",
            height: isAssetsPage
              ? "auto"
              : isNavigationFromAssets
                ? "calc(90vh - 180px)"
                : "calc(100vh - 180px)",
            minHeight: isAssetsPage ? "calc(100vh - 300px)" : "auto",
          }}

          className="scroll"
        >
          {/* Summary */}
          <Box sx={{ width: "100%" }}>
            {(() => {
              const summaryText = cveData.summaryData?.cve_summary
                ?.replace(/\n\s*\n\s*\n+/g, "\n\n")
                .trim() || "";

              const LINUX_KERNEL_PREFIX =
                "In the Linux kernel, the following vulnerability has been resolved:";
              const isLinuxKernel = summaryText.startsWith(LINUX_KERNEL_PREFIX);

              if (isLinuxKernel) {
                const parts = summaryText
                  .slice(LINUX_KERNEL_PREFIX.length)
                  .split("\n\n")
                  .map((p) => p.trim().replace(/\n/g, " "))
                  .filter(Boolean);
                const commitTitle = parts[0] || "";
                const body = parts.slice(1).join("\n\n");
                const previewBody = body.slice(0, 225);

                return (
                  <Box sx={{ mb: 1, p: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-block",
                        mb: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        backgroundColor: isDarkMode ? "#2a2a3a" : "#f0eeff",
                        color: isDarkMode ? "#a89cf7" : "#3A2ADA",
                      }}
                    >
                      Linux Kernel
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, mb: 0.5, wordBreak: "break-word" }}
                    >
                      {commitTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {body ? (
                        showFullSummary ? (
                          <>
                            {body}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                cursor: "pointer",
                                ml: 1,
                                color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
                              }}
                              onClick={() => setShowFullSummary(false)}
                            >
                              Show less
                            </Typography>
                          </>
                        ) : (
                          <>
                            {previewBody}{previewBody.length < body.length ? "..." : ""}
                            {previewBody.length < body.length && (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  cursor: "pointer",
                                  ml: 1,
                                  color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
                                }}
                                onClick={() => setShowFullSummary(true)}
                              >
                                Show more
                              </Typography>
                            )}
                          </>
                        )
                      ) : null}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    p: 1,
                    fontWeight: 500,
                    whiteSpace: "pre-wrap",
                    width: "100%",
                    display: "block",
                    wordBreak: "break-word",
                  }}
                >
                  {summaryText ? (
                    showFullSummary ? (
                      <>
                        {summaryText}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            ml: 1,
                            color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
                          }}
                          onClick={() => setShowFullSummary(false)}
                        >
                          Show less
                        </Typography>
                      </>
                    ) : (
                      <>
                        {summaryText.slice(0, 225)}...
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            ml: 1,
                            color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
                          }}
                          onClick={() => setShowFullSummary(true)}
                        >
                          Show more
                        </Typography>
                      </>
                    )
                  ) : (
                    "No summary available"
                  )}
                </Typography>
              );
            })()}
          </Box>

          {/* Dates and Source */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: isDarkMode ? "#FFFFFF" : "#8480AD",
                  mb: 1,
                }}
              >
                NVD Published Date:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? "#FFFFFF" : "#333333",
                  fontSize: 12,
                }}
              >
                {cveData.summaryData?.nvd_pd
                  ? moment(cveData.summaryData.nvd_pd, "DD/MM/YYYY").format("DD-MMM-YYYY")
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: isDarkMode ? "#FFFFFF" : "#8480AD",
                  fontSize: 12,
                  mb: 1,
                }}
              >
                NVD Last Modified:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? "#FFFFFF" : "#333333",
                  fontSize: 12,
                }}
              >
                {cveData.summaryData?.nvd_lmd
                  ? moment(cveData.summaryData.nvd_lmd, "DD/MM/YYYY").format("DD-MMM-YYYY")
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: isDarkMode ? "#FFFFFF" : "#8480AD",
                  fontSize: 12,
                  mb: 1,
                }}
              >
                Source:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? "#FFFFFF" : "#333333",
                  fontSize: 12,
                }}
              >
                {cveData.summaryData?.source || "N/A"}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          {/* Vulnerability Details Header */}
          {!getCurrentVersionMetrics() ||
            (getCurrentVersionMetrics()?.authority?.length === 0 &&
              Object.keys(getCurrentVersionMetrics()?.score || {}).length ===
              0 &&
              Object.keys(getCurrentVersionMetrics()?.value || {}).length === 0) ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                No vulnerability details available for this CVSS version
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                  Vulnerability Details
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? "#FFFFFF" : "#8480AD",
                        fontSize: 12,
                      }}
                    >
                      CVSS:
                    </Typography>
                    <Select
                      value={cvssVersion}
                      onChange={(e) => {
                        console.log("CVSS dropdown changed:", {
                          from: cvssVersion,
                          to: e.target.value,
                        });
                        setCvssVersion(e.target.value);
                      }}
                      size="small"
                      sx={{
                        minWidth: 80,
                        height: 32,
                        fontSize: 12,
                        fontWeight: 700,
                        ".MuiSelect-select": { py: 0.5, color: "#6366F1" },
                      }}
                    >
                      {cveData.summaryData?.severity_metrics
                        ?.filter((metric: any) => {
                          // Check if the metric has any meaningful data
                          const hasAuthority = metric.authority && metric.authority.length > 0;
                          const hasScoreData = metric.score && Object.keys(metric.score).length > 0;
                          const hasValueData = metric.value && Object.keys(metric.value).length > 0;

                          // Only show if it has meaningful data (not just empty arrays/objects)
                          if (hasAuthority) return true;

                          if (hasScoreData) {
                            // Check if any score data actually has meaningful values
                            const scoreKeys = Object.keys(metric.score);
                            for (const key of scoreKeys) {
                              const scoreData = metric.score[key];
                              if (scoreData && scoreData.length > 0) {
                                // Check if any score item has a meaningful value
                                for (const item of scoreData) {
                                  if (item.name === "cvss" && item.value !== null && item.value !== undefined && item.value > 0) {
                                    return true;
                                  }
                                  if (item.name === "severity" && item.value && item.value !== "Low" && item.value !== "None") {
                                    return true;
                                  }
                                }
                              }
                            }
                          }

                          if (hasValueData) {
                            // Check if any value data actually has meaningful values
                            const valueKeys = Object.keys(metric.value);
                            for (const key of valueKeys) {
                              const values = metric.value[key];
                              if (values && values.length > 0) {
                                // Check if any value has meaningful content (not just empty strings or "None")
                                for (const value of values) {
                                  if (value.value && value.value !== "" && value.value !== "None" && value.value !== "Low") {
                                    return true;
                                  }
                                }
                              }
                            }
                          }

                          return false;
                        })
                        .map(
                          (metric: any, index: number): JSX.Element => {
                            const hasData = (metric.score && Object.keys(metric.score).length > 0) ||
                              (metric.value && Object.keys(metric.value).length > 0);
                            const hasAuthority = metric.authority && metric.authority.length > 0;
                            if (!hasAuthority) return null; // Skip metrics without any data
                            console.log("Rendering CVSS option:", { metric, hasData, hasAuthority });
                            return (
                              <MenuItem key={metric.key} value={metric.key}>
                                {metric.name.split(": ")[1] || metric.name}
                                {/* {!hasAuthority && hasData && " (Data Available)"} */}
                              </MenuItem>
                            );
                          }
                        )}
                    </Select>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? "#FFFFFF" : "#8480AD",
                        fontSize: 12,
                      }}
                    >
                      Authority:
                    </Typography>
                    <Select
                      value={authority}
                      onChange={(e) => {
                        console.log("Authority dropdown changed:", {
                          from: authority,
                          to: e.target.value,
                        });
                        setAuthority(e.target.value);
                      }}
                      size="small"
                      disabled={!getCurrentVersionMetrics()?.authority?.length}
                      sx={{
                        minWidth: 120,
                        height: 32,
                        fontSize: 12,
                        fontWeight: 700,
                        ".MuiSelect-select": { py: 0.5, color: "#6366F1" },
                      }}
                    >
                      {getCurrentVersionMetrics()?.authority?.length > 0 ? (
                        getCurrentVersionMetrics()?.authority?.map(
                          (auth: any, index: number): JSX.Element => (
                            <MenuItem key={auth.key} value={auth.key}>
                              {auth.name}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem value="" disabled>
                          No authorities available for this CVSS version
                        </MenuItem>
                      )}
                    </Select>
                  </Box>
                </Box>
              </Box>
              {/* Three Grid Sections */}
              {/* {!authority && getCurrentVersionMetrics() && (
                <Box sx={{ p: 2, textAlign: "center", mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {getCurrentDataSource() ? 
                      `Showing data from ${getCurrentDataSource()?.toUpperCase()} (no authority specified)` : 
                      "Select an authority to view CVSS details"
                    }
                  </Typography>
                </Box>
              )} */}
              <Grid container spacing={3}>
                {/* CVSS Score */}
                <Grid item xs={2.8}>
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                    }}
                  >
                    {(authority || getCurrentDataSource()) ? (
                      <>
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            border: `10px solid ${getSeverityColor(
                              (currentSeverity as string) || "none"
                            )}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 600,
                              color: getSeverityColor(
                                (currentSeverity as string) || "none"
                              ),
                            }}
                          >
                            {currentScore !== null && currentScore !== undefined
                              ? currentScore
                              : "N/A"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getSeverityColor(
                                (currentSeverity as string) || "none"
                              ),
                              fontWeight: 500,
                            }}
                          >
                            {currentSeverity || "N/A"}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{ color: "text.secondary", fontWeight: 500 }}
                        >
                          CVSS Score
                        </Typography>
                        {!authority && getCurrentDataSource() && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textAlign: "center",
                              mt: 1,
                              fontStyle: "italic"
                            }}
                          >
                            {/* Source: {getCurrentDataSource()?.toUpperCase()} */}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          border: "10px solid #E5E7EB",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                          backgroundColor: "#F9FAFB",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                          }}
                        >
                          No Data Available
                        </Typography>
                      </Box>
                    )}
                  </div>
                </Grid>
                {/* Attack Metrics */}
                <Grid item xs={4.6}>
                  <div
                    style={{
                      padding: 2,
                      height: "100%",
                      borderRadius: 2,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {(authority || getCurrentDataSource()) ? (
                      <Box sx={{ "& > :not(:last-child)": { mb: 1.5 } }}>
                        {attackMetrics.length > 0 ? (
                          <>
                            {attackMetrics.map((metric: any, index: number) => (
                              <Box key={index}>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 0.75,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: "#E2E2F233",
                                  }}
                                >
                                  <Typography variant="caption" color="textSecondary">
                                    {metric.label}
                                  </Typography>
                                  <Chip
                                    label={metric.value}
                                    size="small"
                                    sx={{
                                      color: metric.color,
                                      bgcolor: `${metric.color}15`,
                                      borderRadius: "5px",
                                      height: "20px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                </Paper>
                              </Box>
                            ))}
                            {!authority && getCurrentDataSource() && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textAlign: "center",
                                  mt: 1,
                                  fontStyle: "italic"
                                }}
                              >
                                {/* Source: {getCurrentDataSource()?.toUpperCase()} */}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textAlign: "center",
                              fontStyle: "italic"
                            }}
                          >
                            No attack metrics available
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F9FAFB",
                          borderRadius: 2,
                          border: "2px dashed #E5E7EB",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textAlign: "center",
                            fontStyle: "italic"
                          }}
                        >
                          No data available
                        </Typography>
                      </Box>
                    )}
                  </div>
                </Grid>
                {/* Impact Metrics */}
                <Grid item xs={4.6}>
                  <div style={{ padding: 2, height: "100%", borderRadius: 2 }}>
                    {(authority || getCurrentDataSource()) ? (
                      <Box sx={{ "& > :not(:last-child)": { mb: 1.5 } }}>
                        {impactMetrics.length > 0 ? (
                          <>
                            {impactMetrics.map((metric: any, index: number) => (
                              <Box key={index}>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 0.75,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: "#E2E2F233",
                                  }}
                                >
                                  <Typography variant="caption" color="textSecondary">
                                    {metric.label}
                                  </Typography>
                                  <Chip
                                    label={metric.value}
                                    size="small"
                                    sx={{
                                      color: metric.color,
                                      bgcolor: `${metric.color}15`,
                                      borderRadius: "5px",
                                      height: "20px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                </Paper>
                              </Box>
                            ))}
                            {!authority && getCurrentDataSource() && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textAlign: "center",
                                  mt: 1,
                                  fontStyle: "italic"
                                }}
                              >
                                {/* Source: {getCurrentDataSource()?.toUpperCase()} */}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textAlign: "center",
                              fontStyle: "italic"
                            }}
                          >
                            No impact metrics available
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F9FAFB",
                          borderRadius: 2,
                          border: "2px dashed #E5E7EB",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textAlign: "center",
                            fontStyle: "italic"
                          }}
                        >
                          No data available
                        </Typography>
                      </Box>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Divider sx={{ m: 0, mt: 2, mb: 2 }} />
              {/* EPSS Section */}
              <Typography
                variant="h6"
                sx={{ m: 1, fontWeight: 700, fontSize: 15 }}
              >
                Exploit prediction scoring system (EPSS)
              </Typography>
              <Grid container spacing={0} sx={{ mb: 3 }}>
                <Grid item xs={5}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      pl: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Probability of exploitation activity
                      <br />
                      next 30 days
                    </Typography>
                    <Chip
                      label={`${(
                        (cveData.summaryData?.epss_details?.pea || 0) * 100
                      ).toFixed(2)}%`}
                      sx={{
                        bgcolor: "#D8D4F8",
                        color: "#3A2ADA",
                        fontWeight: "bold",
                        mt: 1,
                        fontSize: 22,
                        // px: 1,
                        // py: 1,
                        borderRadius: "0px",
                        minWidth: 80,
                        justifyContent: "center",
                        fontFamily: "inherit",
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={7}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      pl: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Percentile, the proportion of vulnerabilities that are
                      <br />
                      scored at or less
                    </Typography>
                    <Chip
                      label={`~ ${((cveData.summaryData?.epss_details?.pov || 0) * 100).toFixed(2)}%`}
                      sx={{
                        bgcolor: "#FADCCA",
                        color: "#FF6600",
                        fontWeight: "bold",
                        mt: 1,
                        fontSize: 22,
                        // px: 1,
                        // py: 1,
                        borderRadius: "0px",
                        minWidth: 80,
                        justifyContent: "center",
                        fontFamily: "inherit",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              {/* Reference Section */}
              <Accordion
                sx={{
                  mt: 2,
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  "& .MuiAccordionSummary-root": {
                    p: 0,
                    borderRadius: "8px",
                    "&.Mui-expanded": {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },
                  "& .MuiAccordionDetails-root": {
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2D2D2D" : "#F8F8FF",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      }}
                    />
                  }
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                    "& .MuiAccordionSummary-content": { m: 0, p: 2 },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color:
                        theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      pl: 2,
                    }}
                  >
                    Reference
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={7}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          mb: 1.5,
                          color:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#4B5563",
                        }}
                      >
                        Hyperlinks
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          mb: 1.5,
                          color:
                            theme.palette.mode === "dark"
                              ? "#FFFFFF"
                              : "#4B5563",
                        }}
                      >
                        Resources
                      </Typography>
                    </Grid>

                    {(() => {
                      // Deduplicate references based on link URL
                      const uniqueReferences = cveData.summaryData?.reference?.filter(
                        (ref: any, index: number, self: any[]) =>
                          index === self.findIndex((r: any) => r.hyperlinks.link === ref.hyperlinks.link)
                      ) || [];

                      return uniqueReferences.map((ref: any, index: number) => (
                        <React.Fragment key={`reference-${index}`}>
                          <Grid item xs={7}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#FFFFFF"
                                    : "inherit",
                                "& a": {
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#4F46E5"
                                      : "#4F46E5",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                },
                                wordBreak: "break-all",
                                overflowWrap: "break-word",
                              }}
                            >
                              <a
                                href={ref.hyperlinks.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {ref.hyperlinks.link}
                              </a>
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#FFFFFF"
                                    : "textSecondary",
                                wordBreak: "break-word",
                              }}
                            >
                              {ref.hyperlinks.resources}
                            </Typography>
                          </Grid>
                        </React.Fragment>
                      ));
                    })()}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              {/* Weakness Enumeration Section */}
              <Accordion
                sx={{
                  mt: 2,
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  "& .MuiAccordionSummary-root": {
                    p: 0,
                    borderRadius: "8px",
                    "&.Mui-expanded": {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },
                  "& .MuiAccordionDetails-root": {
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2D2D2D" : "#F8F8FF",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      }}
                    />
                  }
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                    "& .MuiAccordionSummary-content": { m: 0, p: 2 },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color:
                        theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      pl: 2,
                    }}
                  >
                    Weakness Enumeration
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Header Row */}
                    <Grid container sx={{ pl: 3 }}>
                      <Grid item xs={3}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            mb: 1.5,
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFFFFF"
                                : "#4B5563",
                          }}
                        >
                          CWE ID
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            mb: 1.5,
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFFFFF"
                                : "#4B5563",
                          }}
                        >
                          CWE Name
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 500,
                            mb: 1.5,
                            color:
                              theme.palette.mode === "dark"
                                ? "#FFFFFF"
                                : "#4B5563",
                            ml: 2
                          }}
                        >
                          Source
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Data Rows */}
                    {cveData.summaryData?.weakness_enumeration?.map(
                      (weakness: any, index: number) => (
                        <Grid container key={index} sx={{ pl: 3 }}>
                          <Grid item xs={3}>
                            <Typography
                              variant="body2"
                              sx={{
                                cursor: "pointer",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#4F46E5"
                                    : "#3A2ADA",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              <Link
                                href={weakness.cwe_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="always"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#4F46E5"
                                      : "#3A2ADA",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {weakness.cwe_id}
                              </Link>
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Tooltip
                              title={weakness.cwe_name}
                              placement="top"
                              arrow
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  display: "block",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#FFFFFF"
                                      : "inherit",
                                }}
                              >
                                {weakness.cwe_name}
                              </Typography>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1,
                                ml: 2,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#FFFFFF"
                                    : "textSecondary",
                              }}
                            >
                              {weakness.source}
                            </Typography>
                          </Grid>
                        </Grid>
                      )
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              {/* Known Affected Software Configurations panel removed — the SaaS model
                  keeps the affected-CPE enum internal to spark-cve, so the backend no
                  longer sends known_affected_sc and this block never renders. */}
              {(cveData.summaryData?.known_affected_sc?.length ?? 0) > 0 && (
              <Accordion
                sx={{
                  mt: 2,
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  "& .MuiAccordionSummary-root": {
                    p: 0,
                    borderRadius: "8px",
                    "&.Mui-expanded": {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  },
                  "& .MuiAccordionDetails-root": {
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2D2D2D" : "#F8F8FF",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      }}
                    />
                  }
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                    "& .MuiAccordionSummary-content": { m: 0, p: 2 },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color:
                        theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                      pl: 2,
                    }}
                  >
                    Known Affected Software Configurations
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {cveData.summaryData?.known_affected_sc?.map(
                    (config: any, index: number) => (
                      <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                        <Grid item xs={7}>
                          <Box>            <WhiteTooltip title={config.cpe} placement="bottom" arrow >

                            <Typography
                              variant="body2"
                              sx={{
                                // maxWidth: 520,
                                whiteSpace: 'nowrap',
                                cursor: 'default',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-all',   // 🔑 handles * case
                                flexShrink: 0,
                                color: theme.palette.mode === 'dark' ? '#FFFFFF' : 'inherit',
                              }}
                            >
                              {config.cpe}
                            </Typography>
                          </WhiteTooltip>
                            <Typography
                              variant="caption"
                              onClick={() =>
                                handleMatchingCpe(
                                  config.match_criteria_id,
                                  index
                                )
                              }
                              sx={{
                                cursor: "pointer",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#4F46E5"
                                    : "#3A2ADA",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                              >
                                Show Matching CPE(s)
                                {loadingCpeIndex === index ? (
                                  <CircularProgress
                                    size={15}
                                    style={{ marginLeft: 8 }}
                                  />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </div>
                            </Typography>
                            {openCpeIndex === index &&
                              (loadingCpeIndex === index ? (
                                <Box
                                  sx={{
                                    backgroundColor: theme.palette.mode === "dark" ? "#2D2D2D" : "#E5E5FC",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    marginTop: "8px",
                                    display: "flex",
                                    justifyContent: "center",
                                    border: theme.palette.mode === "dark" ? "1px solid #404040" : "none",
                                  }}
                                >
                                  <CircularProgress size={20} />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    backgroundColor: theme.palette.mode === "dark" ? "#2D2D2D" : "#E5E5FC",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    marginTop: "8px",
                                    border: theme.palette.mode === "dark" ? "1px solid #404040" : "none",
                                    color: theme.palette.mode === "dark" ? "#FFFFFF" : "inherit",
                                  }}
                                >
                                  {matchingCpes[index] &&
                                    matchingCpes[index].length > 0 ? (
                                    <>
                                      {matchingCpes[index]
                                        .slice(
                                          0,
                                          expandedIndexes.includes(index)
                                            ? undefined
                                            : 5
                                        )
                                        .map((cpe: string, i: number) => (
                                          <WhiteTooltip title={cpe} placement="bottom" arrow key={i}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                color: theme.palette.mode === "dark" ? "#FFFFFF" : "inherit",
                                                cursor: "default",
                                              }}
                                            >
                                              {cpe}
                                            </Typography>
                                          </WhiteTooltip>
                                        ))}

                                      {matchingCpes[index].length > 5 && (
                                        <Typography
                                          component="div"
                                          sx={{
                                            color: theme.palette.mode === "dark" ? "#4F46E5" : "blue",
                                            cursor: "pointer",
                                            marginTop: "4px",
                                            fontSize: "0.875rem",
                                          }}
                                          onClick={() => {
                                            const isExpanded =
                                              expandedIndexes.includes(index);
                                            setExpandedIndexes((prev) =>
                                              isExpanded
                                                ? prev.filter(
                                                  (i) => i !== index
                                                )
                                                : [...prev, index]
                                            );
                                          }}
                                        >
                                          {expandedIndexes.includes(index)
                                            ? "Show less"
                                            : "Show more"}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <Typography sx={{ color: theme.palette.mode === "dark" ? "#FFFFFF" : "inherit", fontSize: "0.8rem" }}>No matching CPEs available</Typography>
                                  )}
                                </Box>
                              ))}
                          </Box>
                        </Grid>
                        {config.from && (
                          <Grid item xs={2.5}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#FFFFFF"
                                      : "textSecondary",
                                }}
                                gutterBottom
                              >
                                From({config.from_include ? "including" : "excluding"})
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#FFFFFF"
                                      : "inherit",
                                }}
                              >
                                {config.from}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {config.to && (
                          <Grid item xs={2.5}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#FFFFFF"
                                      : "textSecondary",
                                }}
                                gutterBottom
                              >
                                Up to({config.to_include ? "including" : "excluding"})
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#FFFFFF"
                                      : "inherit",
                                }}
                              >
                                {config.to}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    )
                  )}
                </AccordionDetails>
              </Accordion>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CVEDetailsPanel;
